import { Badge, Flex, Text } from '@tremor/react'
import { PhoneIcon, PhoneXMarkIcon } from '@heroicons/react/24/solid'
import type { VoipStatus } from '@/types'

interface VoipStatusBadgeProps {
  status: VoipStatus
  isInCall?: boolean
  showLabel?: boolean
  size?: 'sm' | 'md'
}

function getStatusColor(status: VoipStatus): 'green' | 'red' | 'yellow' | 'gray' | 'blue' {
  switch (status) {
    case 'registered':
      return 'green'
    case 'in_call':
      return 'blue'
    case 'ringing':
      return 'yellow'
    case 'unregistered':
      return 'gray'
    default:
      return 'gray'
  }
}

function getStatusLabel(status: VoipStatus): string {
  switch (status) {
    case 'registered':
      return 'Disponible'
    case 'in_call':
      return 'En appel'
    case 'ringing':
      return 'Sonnerie'
    case 'unregistered':
      return 'Non connecte'
    default:
      return 'Inconnu'
  }
}

function getStatusIcon(status: VoipStatus, size: 'sm' | 'md') {
  const iconClass = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'

  switch (status) {
    case 'in_call':
    case 'ringing':
      return <PhoneIcon className={`${iconClass} text-current`} />
    case 'unregistered':
      return <PhoneXMarkIcon className={`${iconClass} text-current`} />
    default:
      return <PhoneIcon className={`${iconClass} text-current`} />
  }
}

export function VoipStatusBadge({ status, showLabel = false, size = 'sm' }: VoipStatusBadgeProps) {
  const color = getStatusColor(status)
  const label = getStatusLabel(status)
  const icon = getStatusIcon(status, size)

  if (showLabel) {
    return (
      <Badge color={color} size={size} icon={() => icon}>
        {label}
      </Badge>
    )
  }

  // Compact version - just icon with tooltip
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      className={`
        ${size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'}
        rounded-full
        ${color === 'green' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : ''}
        ${color === 'blue' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 animate-pulse' : ''}
        ${color === 'yellow' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400 animate-pulse' : ''}
        ${color === 'gray' ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500' : ''}
        ${color === 'red' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : ''}
      `}
      title={label}
    >
      {icon}
    </Flex>
  )
}

// Simple inline phone indicator for operator cards
interface PhoneIndicatorProps {
  status: VoipStatus
  isInCall: boolean
}

export function PhoneIndicator({ status, isInCall }: PhoneIndicatorProps) {
  if (status === 'unregistered' || status === 'unknown') {
    return (
      <PhoneXMarkIcon
        className="w-4 h-4 text-gray-400"
        title="Telephone non connecte"
      />
    )
  }

  if (isInCall || status === 'in_call') {
    return (
      <PhoneIcon
        className="w-4 h-4 text-blue-500 animate-pulse"
        title="En appel"
      />
    )
  }

  if (status === 'ringing') {
    return (
      <PhoneIcon
        className="w-4 h-4 text-yellow-500 animate-bounce"
        title="Sonnerie"
      />
    )
  }

  // Registered / available
  return (
    <PhoneIcon
      className="w-4 h-4 text-green-500"
      title="Telephone connecte"
    />
  )
}

// Infrastructure status component
interface VoipInfraStatusProps {
  status: 'online' | 'offline' | 'error'
  activeCallsCount?: number
  registeredCount?: number
  totalCount?: number
}

export function VoipInfraStatus({
  status,
  activeCallsCount = 0,
  registeredCount = 0,
  totalCount = 0
}: VoipInfraStatusProps) {
  const isOnline = status === 'online'

  return (
    <Flex justifyContent="start" alignItems="center" className="gap-2">
      <div
        className={`w-2 h-2 rounded-full ${
          isOnline ? 'bg-green-500' : 'bg-red-500'
        }`}
        title={isOnline ? 'Asterisk en ligne' : 'Asterisk hors ligne'}
      />
      <Text className="text-xs text-gray-500">
        {isOnline ? (
          <>
            <span className="font-medium">{activeCallsCount}</span> appels |
            <span className="font-medium"> {registeredCount}</span>/{totalCount} connectes
          </>
        ) : (
          'VoIP hors ligne'
        )}
      </Text>
    </Flex>
  )
}

export default VoipStatusBadge
